import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Briefcase, Plus, Pencil, Trash2, Save, X, CheckSquare, Square, MinusSquare, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

// Same tree used by Permission Master so parents = main menus, leaves map to backend MENU_KEYS.
const TREE = [
  { label: "Dashboard", leaf: "dashboard" },
  { label: "CRM", children: [["customer_master", "Customer Master"], ["feedback", "Feedback"], ["campaigns", "Marketing Campaigns"]] },
  { label: "Transactions", leaf: "transactions" },
  { label: "Workshop", children: [["bookings", "Booking"], ["vehicles", "Vehicle"], ["jobcards", "Job Card"], ["invoices", "Invoices"], ["jobrates", "Rate Card"], ["packages", "Package"], ["reminders", "Reminders"]] },
  { label: "Inventory", children: [["items", "Item Master"], ["companies", "Company Master"], ["categories", "Category Master"], ["groups", "Group Master"], ["rates", "Rate Master"], ["supplier_companies", "Supplier Wise Company"], ["hsn", "HSN Code Master"], ["labels", "Labels"]] },
  { label: "Stocks", leaf: "stocks" },
  { label: "Accounts", children: [["day_book", "Day Book"], ["cash_book", "Cash Book"], ["bank_book", "Bank Book"], ["customer_ledger", "Customer Ledger"], ["vendor_ledger", "Ledger"], ["expenses", "Expenses"], ["vouchers", "Vouchers"], ["gst_reports", "GST Reports"], ["profit_loss", "Profit & Loss"]] },
  { label: "AI Insights", leaf: "insights" },
  { label: "HRMS", leaf: "hrms" },
  { label: "Reports", leaf: "reports" },
  { label: "Org & Access", children: [["organisation", "Organisation Master"], ["users", "User Master"], ["roles", "Role Master"], ["permissions", "Permission Master"]] },
  { label: "Customers (legacy)", leaf: "customers" },
];

const flatKeys = (() => {
  const out = [];
  TREE.forEach(n => n.leaf ? out.push(n.leaf) : n.children.forEach(([k]) => out.push(k)));
  return out;
})();

const emptyMenus = () => Object.fromEntries(flatKeys.map(k => [k, false]));

const Icon = ({ state, ind, ...p }) => (
  <button type="button" {...p} className="p-1">
    {ind ? <MinusSquare className="w-4 h-4 text-primary/60"/>
      : state ? <CheckSquare className="w-4 h-4 text-primary"/>
      : <Square className="w-4 h-4 text-muted-foreground"/>}
  </button>
);

function MenuTreeEditor({ value, onChange, testPrefix = "plan" }) {
  const [expanded, setExpanded] = useState({});
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setMany = (obj) => onChange({ ...value, ...obj });
  return (
    <div className="border border-border bg-card rounded-sm overflow-hidden">
      <table className="w-full text-sm" data-testid={`${testPrefix}-menu-tree`}>
        <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <tr><th className="text-left p-3">Menu / Sub-Item</th><th className="text-center p-3 w-24">Enabled</th></tr>
        </thead>
        <tbody>
          {TREE.map(node => {
            if (node.leaf) return (
              <tr key={node.label} data-testid={`${testPrefix}-row-${node.leaf}`} className="border-t border-border hover:bg-secondary/40">
                <td className="p-3 text-xs font-semibold">{node.label}</td>
                <td className="p-3 text-center">
                  <Icon state={!!value[node.leaf]} data-testid={`${testPrefix}-toggle-${node.leaf}`} onClick={() => set(node.leaf, !value[node.leaf])}/>
                </td>
              </tr>
            );
            const open = expanded[node.label] !== false;
            const slug = node.label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
            const vals = node.children.map(([k]) => !!value[k]);
            const allOn = vals.every(Boolean); const someOn = vals.some(Boolean);
            return [
              <tr key={node.label} data-testid={`${testPrefix}-parent-${slug}`} className="border-t border-border bg-secondary/30">
                <td className="p-3 text-xs font-bold uppercase tracking-wider">
                  <button type="button" data-testid={`${testPrefix}-expand-${slug}`} onClick={() => setExpanded(x => ({ ...x, [node.label]: !open }))} className="flex items-center gap-1.5">
                    {open ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>}
                    {node.label}
                  </button>
                </td>
                <td className="p-3 text-center">
                  <Icon state={allOn} ind={!allOn && someOn} data-testid={`${testPrefix}-toggle-${slug}`}
                    onClick={() => setMany(Object.fromEntries(node.children.map(([k]) => [k, !allOn])))}/>
                </td>
              </tr>,
              ...(open ? node.children.map(([k, label]) => (
                <tr key={k} data-testid={`${testPrefix}-row-${k}`} className="border-t border-border/60 hover:bg-secondary/40">
                  <td className="p-3 pl-9 text-xs text-muted-foreground">{label}</td>
                  <td className="p-3 text-center">
                    <Icon state={!!value[k]} data-testid={`${testPrefix}-toggle-${k}`} onClick={() => set(k, !value[k])}/>
                  </td>
                </tr>
              )) : []),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

export { MenuTreeEditor, TREE, flatKeys, emptyMenus };

export default function PlanMaster() {
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", menus: emptyMenus() });

  const load = () => api.get("/plans").then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", description: "", menus: emptyMenus() }); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ name: p.name, description: p.description || "", menus: { ...emptyMenus(), ...p.menus } });
    setEditId(p.id); setOpen(true);
  };
  const close = () => { setOpen(false); setEditId(null); };
  const enabledCount = useMemo(() => Object.values(form.menus).filter(Boolean).length, [form.menus]);

  const save = async () => {
    try {
      const payload = { name: form.name.trim(), description: form.description || null, menus: form.menus };
      if (editId) await api.patch(`/plans/${editId}`, payload);
      else await api.post("/plans", payload);
      toast.success(editId ? "Plan updated" : "Plan created");
      close(); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (p) => {
    if (!confirm(`Delete plan "${p.name}"?`)) return;
    try { await api.delete(`/plans/${p.id}`); toast.success("Plan deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (open) return (
    <div className="p-8">
      <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
        <h2 className="font-display uppercase text-lg">{editId ? "Edit" : "New"} Plan</h2>
        <Button type="button" data-testid="plan-cancel" variant="ghost" onClick={close} className="rounded-sm text-xs uppercase tracking-wider text-muted-foreground"><X className="w-3 h-3 mr-1"/>Cancel</Button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Plan Name *</Label>
          <Input data-testid="plan-name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="e.g. Starter"/>
        </div>
        <div className="col-span-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description</Label>
          <Input data-testid="plan-desc" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="Optional plan description"/>
        </div>
      </div>
      <div className="text-xs text-muted-foreground mb-2">Enabled menus: <span className="font-semibold text-primary" data-testid="plan-count">{enabledCount}</span> / {flatKeys.length}</div>
      <MenuTreeEditor value={form.menus} onChange={(v) => setForm(f => ({ ...f, menus: v }))} testPrefix="plan"/>
      <div className="flex justify-end gap-2 mt-6 border-t border-border pt-4">
        <Button type="button" variant="ghost" onClick={close} className="rounded-sm text-xs uppercase tracking-wider">Cancel</Button>
        <Button data-testid="plan-save" onClick={save} disabled={!form.name.trim()} className="rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-wider"><Save className="w-3 h-3 mr-1"/> {editId ? "Update Plan" : "Save Plan"}</Button>
      </div>
    </div>
  );

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Briefcase className="w-3 h-3"/> Org & Access</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Plan Master</h1>
          <div className="text-xs text-muted-foreground mt-1">Subscription plans that define which menus organisations can access</div>
        </div>
        <Button data-testid="add-plan-btn" onClick={openNew} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Plus className="w-4 h-4 mr-1"/> New Plan</Button>
      </div>
      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="plans-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr><th className="text-left p-3">Plan Name</th><th className="text-left p-3">Description</th><th className="text-right p-3">Enabled Menus</th><th className="text-right p-3">Actions</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground" data-testid="plans-empty">No plans yet — click New Plan to create one</td></tr>}
            {pg.pageItems.map(p => {
              const cnt = Object.values(p.menus || {}).filter(Boolean).length;
              return (
                <tr key={p.id} data-testid={`plan-row-${p.id}`} className="border-t border-border hover:bg-secondary/40">
                  <td className="p-3 font-semibold">{p.name}</td>
                  <td className="p-3 text-xs text-muted-foreground">{p.description || "-"}</td>
                  <td className="p-3 text-right font-mono">{cnt} / {flatKeys.length}</td>
                  <td className="p-3 text-right space-x-1 whitespace-nowrap">
                    <Button data-testid={`plan-edit-${p.id}`} size="sm" variant="ghost" title="Edit" onClick={() => openEdit(p)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                    <Button data-testid={`plan-del-${p.id}`} size="sm" variant="ghost" title="Delete" onClick={() => del(p)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
